import { Button, Card } from "@tremor/react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ExternalLink,
  Layers,
  Shield,
  Trash2,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "../components/Badge.jsx";
import DeleteSiteDialog from "../components/DeleteSiteDialog.jsx";
import { useSiteDashboard } from "../queries/sites.js";

const STATUS_BADGE_COLOR = {
  up: "emerald",
  degraded: "amber",
  down: "red",
  paused: "gray",
};

const STATUS_RANK = {
  down: 3,
  degraded: 2,
  paused: 1,
  up: 0,
};

const MONITOR_GROUPS = [
  {
    key: "edge",
    label: "Edge",
    description: "DNS, SSL, and domain coverage",
    types: ["dns", "ssl", "domain"],
  },
  {
    key: "origin",
    label: "Origin",
    description: "HTTP, API, ping, and transport checks",
    types: ["http", "api", "ping", "tcp", "port", "cron"],
  },
  {
    key: "journey",
    label: "Journey",
    description: "Shopify, WordPress, and browser flow checks",
    types: ["shopify", "wordpress", "playwright"],
  },
  {
    key: "quality",
    label: "Quality",
    description: "Visual, performance, and content checks",
    types: [
      "visual_change",
      "page_speed",
      "performance_budget",
      "seo_health",
      "broken_links",
      "image_optimization",
      "suspicious_script",
      "carbon_footprint",
    ],
  },
  {
    key: "other",
    label: "Other",
    description: "Everything else",
    types: [],
  },
];

const MONITOR_LABELS = {
  api: "API",
  carbon_footprint: "Carbon",
  cron: "Cron",
  dns: "DNS",
  domain: "Domain",
  broken_links: "Broken links",
  http: "HTTP",
  image_optimization: "Image optimization",
  page_speed: "Page speed",
  performance_budget: "Perf budget",
  ping: "Ping",
  playwright: "Playwright",
  port: "Port",
  seo_health: "SEO health",
  shopify: "Shopify",
  suspicious_script: "Suspicious script",
  tcp: "TCP",
  visual_change: "Visual change",
  wordpress: "WordPress",
  ssl: "SSL",
};

const EMPTY_ARRAY = [];

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function getMonitorGroup(type) {
  return MONITOR_GROUPS.find((group) => group.types.includes(type)) ?? MONITOR_GROUPS.at(-1);
}

function getMonitorLabel(type) {
  return MONITOR_LABELS[type] ?? type.replace(/_/g, " ");
}

function getWorstStatus(statuses) {
  if (!statuses.length) return "up";
  if (statuses.every((status) => status === "paused")) return "paused";
  if (statuses.some((status) => status === "down")) return "down";
  if (statuses.some((status) => status === "degraded")) return "degraded";
  return "up";
}

function sortActionableItems(items) {
  const rank = { critical: 0, warning: 1 };
  return [...items].sort(
    (a, b) =>
      (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9) ||
      String(a.title).localeCompare(String(b.title))
  );
}

function buildMonitorLayers(monitors) {
  const layers = new Map();

  for (const group of MONITOR_GROUPS) {
    layers.set(group.key, {
      ...group,
      monitors: [],
    });
  }

  for (const monitor of monitors) {
    const group = getMonitorGroup(monitor.type);
    layers.get(group.key).monitors.push(monitor);
  }

  return MONITOR_GROUPS.map((group) => {
    const layer = layers.get(group.key);
    if (!layer.monitors.length) {
      return null;
    }

    const monitorsSorted = [...layer.monitors].sort((a, b) => {
      const rankDelta = (STATUS_RANK[b.status] ?? 0) - (STATUS_RANK[a.status] ?? 0);
      if (rankDelta !== 0) return rankDelta;
      return String(a.name).localeCompare(String(b.name));
    });

    const status = getWorstStatus(monitorsSorted.map((monitor) => monitor.status));
    const counts = monitorsSorted.reduce(
      (acc, monitor) => {
        acc.total += 1;
        acc[monitor.status] = (acc[monitor.status] ?? 0) + 1;
        return acc;
      },
      { total: 0, up: 0, degraded: 0, down: 0, paused: 0 }
    );

    return {
      ...group,
      monitors: monitorsSorted,
      status,
      counts,
    };
  }).filter(Boolean);
}

function buildSummary(monitors, actionableItems, site) {
  const counts = monitors.reduce(
    (acc, monitor) => {
      acc.total += 1;
      acc[monitor.status] = (acc[monitor.status] ?? 0) + 1;
      if (monitor.last_response_time_ms != null) {
        acc.response_total += monitor.last_response_time_ms;
        acc.response_count += 1;
      }
      if (monitor.last_check_at) {
        if (!acc.latest_check_at || monitor.last_check_at > acc.latest_check_at) {
          acc.latest_check_at = monitor.last_check_at;
        }
      }
      return acc;
    },
    {
      total: 0,
      up: 0,
      degraded: 0,
      down: 0,
      paused: 0,
      response_total: 0,
      response_count: 0,
      latest_check_at: null,
    }
  );

  const avgResponse =
    counts.response_count > 0 ? counts.response_total / counts.response_count : null;

  const sortedActionables = sortActionableItems(actionableItems);
  const topIssue = sortedActionables[0] ?? null;
  const failingComponents = counts.down + counts.degraded;

  let summary = "This site is healthy across its checked layers.";
  if (site.status === "down" || counts.down > 0) {
    summary = `${failingComponents} component${failingComponents === 1 ? "" : "s"} are failing or degraded.`;
  } else if (counts.degraded > 0) {
    summary = `${counts.degraded} component${counts.degraded === 1 ? "" : "s"} are degraded, but the stack is still serving traffic.`;
  }

  if (topIssue) {
    summary += ` Top diagnostic: ${topIssue.title}.`;
  }

  return {
    counts,
    avgResponse,
    latestCheckAt: counts.latest_check_at,
    actionableCount: sortedActionables.length,
    topIssue,
    summary,
  };
}

function severityStyle(severity) {
  if (severity === "critical") {
    return {
      bg: "bg-red-50 border-red-200",
      text: "text-red-900",
      badge: "red",
      icon: <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />,
    };
  }

  return {
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-900",
    badge: "amber",
    icon: <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />,
  };
}

function MetricCard({ icon: Icon, label, value, hint }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <Icon className="h-5 w-5 text-teal-600" />
      </div>
      <p className="mt-3 text-3xl font-semibold text-gray-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </Card>
  );
}

function ActionableCard({ item }) {
  const style = severityStyle(item.severity);

  return (
    <div className={`rounded-lg border p-4 ${style.bg}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          {style.icon}
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={`text-sm font-bold ${style.text}`}>{item.title}</h3>
              <Badge color={style.badge}>{item.severity.toUpperCase()}</Badge>
              {item.monitor_type && (
                <span className="rounded-full border border-black/5 bg-white/70 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                  {getMonitorLabel(item.monitor_type)}
                </span>
              )}
            </div>
            <p className="max-w-3xl text-xs leading-relaxed text-gray-600">
              {item.description}
            </p>
          </div>
        </div>
        {item.action_link && (
          <Link to={item.action_link} className="flex-shrink-0">
            <Button variant="secondary" icon={ChevronRight} iconPosition="right" size="xs">
              View details
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function MonitorRow({ monitor }) {
  return (
    <Link
      to={`/monitors/${monitor.id}`}
      className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">{monitor.name}</span>
          <Badge color={STATUS_BADGE_COLOR[monitor.status] ?? "gray"}>
            {monitor.status.toUpperCase()}
          </Badge>
          <Badge color="gray">{getMonitorLabel(monitor.type)}</Badge>
        </div>
        <p className="mt-1 truncate text-xs text-gray-500">{monitor.target}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3 text-xs text-gray-400">
        <span>{fmtTime(monitor.last_check_at)}</span>
        {monitor.last_response_time_ms != null && (
          <span className="font-mono text-gray-500">{fmtMs(monitor.last_response_time_ms)}</span>
        )}
        <ChevronRight className="h-4 w-4 text-gray-300" />
      </div>
    </Link>
  );
}

function LayerCard({ layer }) {
  const statusColor = STATUS_BADGE_COLOR[layer.status] ?? "gray";

  return (
    <Card className="p-0">
      <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{layer.label}</h3>
            <Badge color={statusColor}>{layer.status.toUpperCase()}</Badge>
          </div>
          <p className="mt-1 text-xs text-gray-500">{layer.description}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
          <span>
            {layer.counts.total} component{layer.counts.total === 1 ? "" : "s"}
          </span>
          {layer.counts.down > 0 && (
            <span className="text-red-600">{layer.counts.down} down</span>
          )}
          {layer.counts.degraded > 0 && (
            <span className="text-amber-600">{layer.counts.degraded} degraded</span>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {layer.monitors.map((monitor) => (
          <MonitorRow key={monitor.id} monitor={monitor} />
        ))}
      </div>
    </Card>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dashboardQuery = useSiteDashboard(id);
  const [showDelete, setShowDelete] = useState(false);

  const data = dashboardQuery.data;
  const loading = dashboardQuery.isPending;
  const site = data?.site ?? null;
  const monitors = data?.monitors ?? EMPTY_ARRAY;
  const actionable_items = data?.actionable_items ?? EMPTY_ARRAY;
  const summary = useMemo(() => {
    if (!site) {
      return {
        counts: {
          total: 0,
          up: 0,
          degraded: 0,
          down: 0,
          paused: 0,
        },
        avgResponse: null,
        latestCheckAt: null,
        actionableCount: 0,
        topIssue: null,
        summary: "This site is healthy across its checked layers.",
      };
    }
    return buildSummary(monitors, actionable_items, site);
  }, [monitors, actionable_items, site]);
  const layers = useMemo(() => buildMonitorLayers(monitors), [monitors]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/sites" className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
        </div>
        <p className="text-sm text-gray-400">Loading site details...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6 py-20 text-center">
        <p className="text-base text-gray-600">Site not found.</p>
        <Link to="/sites">
          <Button variant="secondary">Back to Sites</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/sites" className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-bold text-gray-900">{site.name}</h1>
              <Badge color={STATUS_BADGE_COLOR[site.status] ?? "gray"}>
                {site.status.toUpperCase()}
              </Badge>
            </div>
            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center gap-1 text-xs font-medium font-mono text-teal-600 hover:text-teal-700"
            >
              <span className="truncate">{site.url}</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        <Button
          variant="secondary"
          color="red"
          icon={Trash2}
          onClick={() => setShowDelete(true)}
        >
          Delete Site
        </Button>
      </div>

      {showDelete && (
        <DeleteSiteDialog
          site={{ ...site, monitor_count: monitors.length }}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/sites")}
        />
      )}

      <Card>
        <h2 className="text-base font-semibold text-gray-900">
          {site.status === "up"
            ? "The full stack is healthy."
            : "Something in the stack needs attention."}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">{summary.summary}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
          <span className="rounded-full bg-gray-100 px-2.5 py-1">
            Last update: {fmtTime(summary.latestCheckAt ?? site.updated_at)}
          </span>
          {summary.avgResponse != null && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1">
              Average response: {fmtMs(summary.avgResponse)}
            </span>
          )}
          <span className="rounded-full bg-gray-100 px-2.5 py-1">
            Site updated: {fmtTime(site.updated_at)}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {layers.map((layer) => (
            <span
              key={layer.key}
              className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
            >
              {layer.label}: {layer.counts.total} component{layer.counts.total === 1 ? "" : "s"}
            </span>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          icon={Layers}
          label="Components"
          value={summary.counts.total}
          hint="All checks attached to this site."
        />
        <MetricCard
          icon={CheckCircle}
          label="Healthy"
          value={summary.counts.up}
          hint="Passing components."
        />
        <MetricCard
          icon={AlertTriangle}
          label="Issues"
          value={summary.counts.down + summary.counts.degraded}
          hint="Failing or degraded components."
        />
        <MetricCard
          icon={TrendingUp}
          label="Diagnostics"
          value={summary.actionableCount}
          hint="Actionable items from the classifier."
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-gray-900">Actionable diagnostics</h2>
            <p className="mt-1 text-sm text-gray-500">
              Only real failures and useful merchant-facing issues are shown here.
            </p>
          </div>
          <Shield className="h-5 w-5 text-emerald-500" />
        </div>

        {summary.actionableCount === 0 ? (
          <Card className="flex flex-col items-center justify-center border border-gray-100 py-12 text-center shadow-sm">
            <div className="mb-3 rounded-full bg-emerald-50 p-3">
              <Shield className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-base font-semibold text-gray-700">No actionable issues</p>
            <p className="mt-1 max-w-md text-xs leading-5 text-gray-400">
              The current stack is healthy, and nothing on the site needs attention right now.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortActionableItems(actionable_items).map((item) => (
              <ActionableCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-base font-bold text-gray-900">Component layers</h2>
          <p className="mt-1 text-sm text-gray-500">
            The site is presented as a stack of ordered health layers instead of a flat monitor list.
          </p>
        </div>

        <div className="space-y-4">
          {layers.map((layer) => (
            <LayerCard key={layer.key} layer={layer} />
          ))}
        </div>
      </div>
    </div>
  );
}
